/**
 * Reglas de presupuesto que se aplican al guardar una CAMPAÑA.
 *
 * Hermano de `strategy-budget.utils.ts`, que hace lo propio para los line items. Vive fuera del
 * componente por la misma razón: la lógica estaba duplicada palabra por palabra en los dos caminos
 * de guardado de `campaign-details.component.ts`, y arreglar una copia no arreglaba nada.
 */

/** El `schedule` que viaja en el diff. El formulario nos da valores sin tipar. */
type ScheduleLike = { [key: string]: unknown } | null | undefined;

/** Campos de presupuesto que la sección Budget de una campaña puede tocar. */
export const CAMPAIGN_BUDGET_FIELDS = [
    "max_daily_cost",
    "max_daily_impressions",
    "max_daily_clicks",
    "max_global_cost",
    "max_global_impressions",
    "max_global_clicks",
] as const;

/** Lo que el componente lee del formulario para poder decidir. */
export interface CampaignBudgetContext {
    /** Valor del radio Lifetime/Daily. */
    budgetType: unknown;
    /**
     * Si el trader ha tocado el input de límite diario **en esta edición**.
     *
     * Tiene que venir de `dirty`, NUNCA de `touched`: `validateData()` llama a `markAllAsTouched()`
     * justo antes de guardar, así que `touched` es cierto siempre y no distingue nada. `dirty` solo
     * lo marca la interacción real — un `patchValue` programático, que es como llega el valor
     * calculado por el DSP, no lo toca.
     */
    dailyCostDirty: boolean;
    /** Valor actual del input de límite diario. */
    dailyCostValue: unknown;
}

/** Lo que el formulario debe mostrar tras releer la campaña del backend. */
export interface CampaignBudgetFormState {
    budget_type: "lifetime" | "daily";
    /** `undefined` cuando el backend no lo manda: no hay nada que patchear. */
    daily_limits_type?: unknown;
}

/** Normaliza un importe del schedule: 0 para vacío, no numérico o no positivo. */
function toPositiveNumber(value: unknown): number {
    if (value === null || value === undefined || value === "") {
        return 0;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
}

/**
 * Deriva el estado del bloque de presupuesto a partir del `schedule` que devuelve el backend.
 *
 * **El modo de reparto lo decide el DSP y viaja en su respuesta.** Releer la campaña no es ocasión
 * para reinventarlo, y hacerlo costó dos incidencias distintas:
 *
 * - Pisar con `"manual"` un `auto` en el que MediaSmart estaba repartiendo congela el gasto diario en
 *   el reparto del presupuesto anterior (ver el docblock de `applyCampaignDailyLimitsType`).
 * - Declarar `"manual"` en una campaña que no tiene ningún cap diario la deja **sin poder entregar**.
 *   Es el caso que reportó Marta Maldonado (MediaSmart) el 11/08/2026: campañas creadas desde
 *   Geoactivate con el presupuesto diario vacío que no arrancan.
 *
 * Por eso `daily_limits_type` se devuelve tal cual viene, y `undefined` si no viene.
 */
export function resolveBudgetFormState(schedule: ScheduleLike): CampaignBudgetFormState {
    const hasLifetimeCost = toPositiveNumber(schedule?.["max_global_cost"]) > 0;
    const hasAnyDailyCap =
        toPositiveNumber(schedule?.["max_daily_cost"]) > 0 ||
        toPositiveNumber(schedule?.["max_daily_impressions"]) > 0 ||
        toPositiveNumber(schedule?.["max_daily_clicks"]) > 0;

    return {
        // Sin señales en ninguno de los dos sentidos manda el default del formulario, que es Lifetime
        // desde el cambio del 10/08/2026.
        budget_type: !hasLifetimeCost && hasAnyDailyCap ? "daily" : "lifetime",
        daily_limits_type: schedule?.["daily_limits_type"],
    };
}

/** ¿Toca el diff algún campo de presupuesto? Un `null` explícito cuenta: limpia un cap. */
export function hasCampaignBudgetChanges(changedSchedule: ScheduleLike): boolean {
    if (!changedSchedule) {
        return false;
    }

    return CAMPAIGN_BUDGET_FIELDS.some((field) => changedSchedule[field] !== undefined);
}

/**
 * Fija (o deja intacto) el `daily_limits_type` del payload de una campaña.
 *
 * El motivo de que exista: con el modo en `auto`, el `max_daily_cost` **no es una decisión del
 * trader** — es el reparto que calcula MediaSmart (17.000 € / 7 días = 2.429) y devuelve en su
 * respuesta, y el formulario lo pinta en el input como cualquier otro valor. Mirar solo si el input
 * tiene contenido convertía ese número calculado en un límite "manual" en cuanto se guardaba
 * cualquier otra cosa; el DSP dejaba de repartir y el diario se quedaba clavado en el reparto del
 * presupuesto ANTERIOR. Una campaña de 21.000 € gastando 1.000 x 7 = 7.000 €, con la pantalla
 * aparentemente correcta (medido el 10/08/2026 — docs/testing/AUDIT_LIFETIME_BUDGET.md).
 *
 * Por eso el modo solo se declara cuando el trader ha tocado el campo. Si no lo ha tocado, no se
 * envía `daily_limits_type` y el DSP conserva el suyo, que es justamente el que sabe repartir.
 *
 * Muta `changedSchedule` en sitio, que es lo que espera el diff que se manda a la API.
 */
export function applyCampaignDailyLimitsType(changedSchedule: ScheduleLike, context: CampaignBudgetContext): void {
    if (!changedSchedule || !hasCampaignBudgetChanges(changedSchedule)) {
        return;
    }

    // Presupuesto diario declarado: el modo es manual por definición, y las cifras lifetime se
    // limpian porque el radio las acaba de descartar.
    if (context.budgetType === "daily") {
        changedSchedule["daily_limits_type"] = "manual";
        changedSchedule["max_global_cost"] = null;
        changedSchedule["max_global_impressions"] = null;
        changedSchedule["max_global_clicks"] = null;
        return;
    }

    if (!context.dailyCostDirty) {
        return;
    }

    const dailyCost = Number(context.dailyCostValue);
    const traderSetACap = context.dailyCostValue !== null && context.dailyCostValue !== "" && Number.isFinite(dailyCost) && dailyCost > 0;

    changedSchedule["daily_limits_type"] = traderSetACap ? "manual" : "auto";
}

/** Las cifras diarias con las que MediaSmart reparte el total de una campaña en `auto`. */
export interface AutoDailyShare {
    cost: number | null;
    impressions: number | null;
    clicks: number | null;
}

const DAILY_CAP_FIELDS = ["max_daily_cost", "max_daily_impressions", "max_daily_clicks"] as const;

/**
 * ¿Son los diarios de este `schedule` de campaña el reparto automático de MediaSmart y no topes?
 *
 * La regla única que comparten el formulario (`splitAutoDailyShare`) y la tabla de Line Items, para
 * que los dos digan lo mismo de la misma campaña: MediaSmart, `auto` y un total que repartir.
 *
 * **Perion nunca.** La API guarda `daily_limits_type: "auto"` en toda campaña Perion
 * (`TransformPerionResponseToCampaignDataAction`) y al reimportarla escribe en `max_daily_cost` el
 * cap REAL de Hivestack (`ImportCampaignAction`): ahí no hay reparto que apartar. Sin
 * `dsp_provider` cuenta como MediaSmart, igual que en el resto del SPA (`hasDailyCostData`).
 */
export function isMediaSmartAutoDailyShare(schedule: ScheduleLike, dspProvider: unknown): boolean {
    return dspProvider !== "perion" && schedule?.["daily_limits_type"] === "auto" && toPositiveNumber(schedule?.["max_global_cost"]) > 0;
}

/**
 * Separa el reparto automático de MediaSmart de los topes diarios de la campaña.
 *
 * Con la campaña en `auto` y un total, MediaSmart calcula el gasto diario él mismo y lo devuelve en
 * su respuesta como `max_daily_cost` / `max_daily_impressions` (300 € en 8 días → 38 y, con
 * 2.300.000 impresiones, 287.500); la API guarda esa respuesta tal cual. Son las mismas claves que
 * un tope puesto a mano, pero no significan lo mismo: en `auto` MediaSmart recalcula todos los
 * diarios y no existe un modo mixto (Globant La Liga, 04/09/2026). Metidas en el formulario abrían
 * el bloque opcional "Add maximum daily limit" con el reparto dentro, como si alguien lo hubiera
 * fijado, y retocarlas convertía la campaña en `manual` sin que nadie lo decidiera (ticket Alba
 * Palenzuela, 08/10/2026; medido en prod el 13/10: 297 de 317 campañas en `auto` tienen esa forma).
 *
 * Devuelve el `schedule` con los tres diarios a `null` **de forma explícita** —un `patchValue` sin
 * la clave dejaría en el control el valor de la campaña anterior— y el reparto aparte, para pintarlo
 * como información. Tiene que aplicarse antes de que el formulario tome su foto inicial: el diff es
 * por valor contra esa foto, así que con los dos lados vacíos nada viaja salvo lo que el trader
 * escriba (y escribir un coste ya declara `manual`, ver `applyCampaignDailyLimitsType`).
 *
 * Fuera de `auto`, sin total o en Perion (ver `isMediaSmartAutoDailyShare`), devuelve el `schedule`
 * sin tocar y ningún reparto.
 */
export function splitAutoDailyShare<T extends ScheduleLike>(
    schedule: T,
    dspProvider: unknown
): { schedule: T; share: AutoDailyShare | null } {
    if (!isMediaSmartAutoDailyShare(schedule, dspProvider)) {
        return { schedule, share: null };
    }

    const [cost, impressions, clicks] = DAILY_CAP_FIELDS.map((field) => toPositiveNumber(schedule[field]) || null);
    const withoutShare = { ...schedule } as Record<string, unknown>;
    for (const field of DAILY_CAP_FIELDS) {
        withoutShare[field] = null;
    }

    return {
        schedule: withoutShare as T,
        share: cost === null && impressions === null && clicks === null ? null : { cost, impressions, clicks },
    };
}
